import { useRef, useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import { FieldError } from '@/shared/components/ui/field-error'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  getBackendFieldErrors,
  parseZod,
  scrollToFirstError,
} from '@/shared/lib/formValidation'
import { toast } from '@/shared/lib/toast'
import { cn } from '@/shared/lib/utils'

import { useMarketing } from '../../hooks/useMarketing'
import { personFormSchema } from '../../schemas/person.schema'

export function MarketingForm({ marketing, onCancel, onSuccess }) {
  const submitLockRef = useRef(false)
  const [formData, setFormData] = useState(() => ({
    name: marketing?.name || '',
    email: marketing?.email || '',
    phoneNumber: marketing?.phoneNumber || '',
  }))
  const [errors, setErrors] = useState({})

  const { createMarketingPerson, updateMarketingPerson, isLoading } =
    useMarketing()

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    clearError(name)

    let sanitized = value
    if (name === 'name') {
      sanitized = value.replace(/[^a-zA-Z0-9\s.'-]/g, '')
    } else if (name === 'phoneNumber') {
      sanitized = value.replace(/\D/g, '').slice(0, 10)
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitized,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isLoading || submitLockRef.current) return
    submitLockRef.current = true

    const parsed = parseZod(personFormSchema, formData)

    if (!parsed.success) {
      submitLockRef.current = false
      setErrors(parsed.errors)
      toast.error(parsed.message)
      scrollToFirstError()
      return
    }

    try {
      setErrors({})

      if (marketing) {
        await updateMarketingPerson(marketing._id, parsed.data)
      } else {
        await createMarketingPerson(parsed.data)
      }

      onSuccess?.()
    } catch (error) {
      setErrors(getBackendFieldErrors(error))
    } finally {
      submitLockRef.current = false
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="mt-6 space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="space-y-2">
          <Label htmlFor="name">FULL NAME</Label>

          <Input
            id="name"
            name="name"
            placeholder="Enter full name"
            value={formData.name}
            onChange={handleChange}
            aria-invalid={Boolean(errors.name)}
            data-invalid={errors.name ? 'true' : undefined}
            className={cn(
              'h-12 rounded-xl',
              errors.name && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
          <FieldError message={errors.name} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">EMAIL</Label>

          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Enter email"
            value={formData.email}
            onChange={handleChange}
            aria-invalid={Boolean(errors.email)}
            data-invalid={errors.email ? 'true' : undefined}
            className={cn(
              'h-12 rounded-xl',
              errors.email && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
          <FieldError message={errors.email} />
        </div>

        <div className="col-span-1 sm:col-span-2 space-y-2">
          <Label htmlFor="phoneNumber">PHONE NUMBER</Label>

          <Input
            id="phoneNumber"
            name="phoneNumber"
            maxLength={10}
            placeholder="Enter 10-digit phone number"
            value={formData.phoneNumber}
            onChange={handleChange}
            aria-invalid={Boolean(errors.phoneNumber)}
            data-invalid={errors.phoneNumber ? 'true' : undefined}
            className={cn(
              'h-12 rounded-xl',
              errors.phoneNumber && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
          <FieldError message={errors.phoneNumber} />
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 sm:gap-4 border-t pt-6">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={onCancel}
          disabled={isLoading}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="ghost"
          disabled={isLoading}
          className="w-full sm:w-auto gap-2 bg-[#0F172A] text-white hover:text-slate-500"
        >
          {isLoading
            ? marketing
              ? 'Updating...'
              : 'Creating...'
            : marketing
              ? 'Update Marketing'
              : 'Create Marketing'}
        </Button>
      </div>
    </form>
  )
}
